import React from 'react';
import ReactMarkdown, { defaultUrlTransform } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import SectionCard from '@/components/SectionCard';
import { pxToRem } from '@/modules/config/constants';

// react-markdown's default url sanitizer strips `sms:`/`tel:` links. Allow
// those two schemes through (they're gated again in SafeLink) while delegating
// every other URL to the default transform so unsafe schemes stay sanitized.
export const bodyUrlTransform = (url: string): string =>
  /^(sms:|tel:)/i.test(url) ? url : defaultUrlTransform(url);

export const SafeLink = ({
  href,
  children,
  ...props
}: React.AnchorHTMLAttributes<HTMLAnchorElement>) => {
  const safe = href && /^(https?:|mailto:|sms:|tel:)/i.test(href);
  if (!safe) return <span>{children}</span>;
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" {...props}>
      {children}
    </a>
  );
};

// Both the margin and the font-size MUST live here on the <p>, because Ionic's
// card typography targets the paragraph element directly: `.card-content-ios p`
// sets `margin-bottom: 2px` and `font-size: 0.875rem` (~14px). A rule on the
// element itself beats anything merely *inherited* from a wrapping div, so
// sizing/spacing an ancestor has no visible effect — only an inline style on
// the <p> overrides it. Restore readable paragraph spacing and size here.
const MarkdownParagraph = ({
  children,
  ...props
}: React.HTMLAttributes<HTMLParagraphElement>) => (
  <p
    style={{ margin: '0 0 0.75em', fontSize: pxToRem(17), lineHeight: 1.5 }}
    {...props}
  >
    {children}
  </p>
);

interface MarkdownBodyProps {
  body: string;
  /** Optional testid on the content container (the component-driven view uses
   *  `topic-readonly-body` for back-compat with its existing specs). */
  containerTestId?: string;
}

/**
 * The topic body rendered as GitHub-flavored Markdown with link
 * sanitization. Shared by the legacy `TopicView` view branch and the
 * component-driven per-type read bodies. The caller guards the empty case.
 *
 * The body is content, not a list-row label, so it renders as a
 * plain content block in the card rather than inside <IonItem>/<IonLabel> — the
 * latter would impose Ionic's compact list-row typography (fixed ~14px on child
 * <p>), which is why sizing an ancestor div had no effect. The text size lives
 * on the rendered <p> (see MarkdownParagraph).
 */
const MarkdownBody: React.FC<MarkdownBodyProps> = ({
  body,
  containerTestId,
}) => (
  <SectionCard>
    <div
      style={{
        padding: '12px 16px',
        color: '#000',
        whiteSpace: 'normal',
        userSelect: 'text',
      }}
      data-testid={containerTestId}
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        urlTransform={bodyUrlTransform}
        components={{ a: SafeLink, p: MarkdownParagraph }}
      >
        {body}
      </ReactMarkdown>
    </div>
  </SectionCard>
);

export default MarkdownBody;
